/**
 * TanStack Query offline persistence.
 * Caches query data in localStorage so it survives page closes.
 *
 * Online behaviour: cache is restored immediately for instant rendering, then
 * all queries are invalidated so fresh data is fetched from the server in the
 * background. Coming back online also triggers a full invalidation.
 *
 * Offline behaviour: cached data is used as-is until connectivity returns.
 */
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { queryClient } from "./queryClient";

const CACHE_KEY = "kintsugi-query-cache";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

let initialized = false;

export function initQueryPersistence() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  try {
    const persister = createSyncStoragePersister({
      storage: window.localStorage,
      key: CACHE_KEY,
    });

    const [, restorePromise] = persistQueryClient({
      queryClient,
      persister,
      maxAge: MAX_AGE_MS,
      // Only persist read queries — never mutations
      dehydrateOptions: {
        shouldDehydrateQuery: (query) => {
          const key = query.queryKey[0];
          if (typeof key !== "string") return false;
          // Exclude auth and mutation-adjacent endpoints
          if (key.startsWith("/api/auth")) return false;
          // Exclude real-time collaborative data — stale feedback causes wrong groupings
          if (key === "/api/feedback") return false;
          return query.state.status === "success";
        },
      },
    });

    // Once the persisted cache is restored, immediately refetch if online so
    // every device always shows the latest server data.
    restorePromise.then(() => {
      if (navigator.onLine) {
        queryClient.invalidateQueries();
      }
    });

    // When the device comes back online after being offline, sync with the server.
    window.addEventListener("online", () => {
      queryClient.invalidateQueries();
    });
  } catch {
    // localStorage unavailable in some environments — degrade gracefully
  }
}
